import { forwardRef, useEffect, useState, type ComponentPropsWithoutRef } from 'react';

import logoIconUrl from '../../assets/brand/logo-tankef-icon.svg';
import logoPayDarkUrl from '../../assets/brand/logo-tankef-pay-dark.svg';
import logoPayUrl from '../../assets/brand/logo-tankef-pay.svg';
import { cn } from '../../utils';

export type LogoVariant = 'icon' | 'pay';
export type LogoSize = 'sm' | 'md' | 'lg' | 'xl';
export type LogoSurface = 'auto' | 'light' | 'dark';

export interface LogoProps extends Omit<ComponentPropsWithoutRef<'img'>, 'children' | 'src'> {
  /**
   * Which brand mark to render. Default: 'pay'.
   *
   * Para la marca dentro del contenido de una pantalla, usa `LogoBrand`: es
   * SVG inline y toma el color del tema, en vez de necesitar una copia del
   * archivo por superficie.
   */
  variant?: LogoVariant;
  /** Preset height. Default: 'md'. Ignored when `height` is set explicitly. */
  size?: LogoSize;
  /** Surface where the logo sits. `dark` swaps to the high-contrast pay wordmark. */
  surface?: LogoSurface;
  /**
   * @deprecated Use `variant` instead. `isSingle={true}` maps to `variant="icon"`.
   */
  isSingle?: boolean;
  /**
   * @deprecated No-op — Logo no longer wraps itself in `<a>`. Wrap with your
   * router's Link component if you need navigation.
   */
  disableLink?: boolean;
}

const sizeHeights: Record<LogoSize, number> = {
  sm: 32,
  md: 48,
  lg: 64,
  xl: 80,
};

// Vite returns URL strings; Next.js/Turbopack returns module objects with a
// `default` or `src` property. Normalise both to a plain string.
function resolveAsset(asset: unknown): string {
  if (typeof asset === 'string') return asset;
  if (asset && typeof asset === 'object') {
    const mod = asset as Record<string, unknown>;
    if (typeof mod.default === 'string') return mod.default;
    if (typeof mod.src === 'string') return mod.src;
  }
  return '';
}

const sources: Record<LogoVariant, string> = {
  icon: resolveAsset(logoIconUrl),
  pay: resolveAsset(logoPayUrl),
};

/**
 * Tankef brand logo.
 *
 * ```tsx
 * <Logo variant="pay" size="lg" />
 * <Logo variant="icon" size="sm" />
 * ```
 */
export const Logo = forwardRef<HTMLImageElement, LogoProps>(
  (
    {
      variant,
      size = 'md',
      surface = 'auto',
      isSingle,
      disableLink: _disableLink,
      className,
      alt = 'Tankef',
      height,
      style,
      ...rest
    },
    ref
  ) => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const resolvedVariant: LogoVariant = variant ?? (isSingle ? 'icon' : 'pay');
    const resolvedHeight = height ?? sizeHeights[size];
    const useDarkSurface = surface === 'dark' || (surface === 'auto' && isDarkMode);
    const resolvedSrc =
      resolvedVariant === 'pay' && useDarkSurface
        ? resolveAsset(logoPayDarkUrl)
        : sources[resolvedVariant];

    useEffect(() => {
      if (surface !== 'auto') return undefined;
      if (typeof document === 'undefined') return undefined;

      const root = document.documentElement;
      const syncTheme = () => setIsDarkMode(root.classList.contains('dark'));

      syncTheme();

      const observer = new MutationObserver(syncTheme);
      observer.observe(root, { attributes: true, attributeFilter: ['class'] });

      return () => observer.disconnect();
    }, [surface]);

    return (
      <img
        ref={ref}
        src={resolvedSrc}
        alt={alt}
        className={cn('inline-block select-none shrink-0', className)}
        style={{ height: resolvedHeight, width: 'auto', ...style }}
        {...rest}
      />
    );
  }
);

Logo.displayName = 'Logo';
